/**
 * Welche Vorlage eine Etappenseite bekommt, und was auf ihr Platz hat.
 *
 * Das Kernproblem des ganzen Druckbilds sind ungleiche Textmengen: eine Etappe
 * hat drei Zeilen Tagebuch, die nächste dreissig. Wer beides in dieselbe
 * Vorlage zwingt, bekommt einmal eine halbleere und einmal eine überfüllte
 * Seite. Gedruckte Reisebücher lösen das nicht durch Schrumpfen der Schrift,
 * sondern durch die Wahl der Vorlage: kurzer Text bekommt grössere Fotos,
 * langer Text läuft auf eine zweite Seite weiter.
 *
 * Diese Wahl muss vor dem Setzen fallen, denn CSS kann Textlängen nicht
 * messen. Es gibt keine Eigenschaft, die "wenn dieser Absatz länger als die
 * Spalte ist, nimm ein anderes Raster" ausdrückt - Container Queries messen den
 * Platz, nicht den Inhalt. Also entscheidet JavaScript, und zwar hier: als reine
 * Funktion aus Textmenge, Fotoanzahl und Planungsdaten, ohne DOM, ohne React,
 * prüfbar.
 *
 * Gerechnet wird in Zeilen, nicht in Zeichen.
 *
 * Das ist der Unterschied, an dem die erste Fassung dieses Moduls gescheitert
 * ist. Sie zählte Zeichen und eichte sie an Fliesstext - 680 Zeichen seien elf
 * Zeilen. Gesetzt wird der Text aber mit white-space:pre-wrap (.dr-text in
 * print.css), die Zeilenumbrüche des Nutzers bleiben also stehen. Ein Tagebuch
 * in Notizform,
 *
 *     07:30 Abfahrt
 *     09:00 Grenze Karawanken
 *     12:15 Mittag am See
 *
 * hat bei 800 Zeichen vierzig Zeilen und nicht zehn. Die Seitenplanung hielt
 * 680 davon für eine Seite voll, gesetzt wurden vierunddreissig Zeilen auf
 * einen Platz von zehn, und overflow:hidden schnitt den Rest weg - ohne
 * Fortsetzungsseite und ohne Hinweis. Genau dieser Schreibstil entsteht abends
 * unterwegs; er ist der Regelfall und nicht der Sonderfall.
 *
 * Deshalb ist die Währung dieses Moduls die Zeile: ein "\n" beendet eine, und
 * ein Abschnitt von 130 Zeichen zählt als zwei. Zeichen kommen nur noch an
 * einer Stelle vor, nämlich in der Frage, wie viele davon in eine Zeile
 * passen - und diese Zahl ist gemessen, siehe ZEICHEN_JE_ZEILE.
 *
 * Ein Tag ist mehrere Beiträge.
 *
 * Seit das Tagebuch aus Beiträgen mit Verfasser besteht, ist der Text einer
 * Etappe keine Zeichenkette mehr, sondern eine Folge davon - und jeder Beitrag
 * kann eine Verfasserzeile tragen und lässt Luft zum nächsten. Beides kostet
 * Platz, und beides muss dieser Rechnung bekannt sein. Rechnete sie weiter mit
 * einem einzelnen Text, wäre der Fehler genau derselbe wie oben: gesetzt würde
 * mehr, als gerechnet wurde, und overflow:hidden schnitte den Rest weg.
 *
 * Der Vorspann kostet dabei keine eigene Zeile. Er steht in der Zeile, wie die
 * Zeile "VON ... —" in einer Zeitschrift, und der Text läuft dahinter weiter -
 * das Buch soll den Tag als einen Text zeigen und nicht als Protokoll. Er
 * kostet also Zeichen und keine Zeilen, und weil er in Versalien und gesperrt
 * gesetzt ist, kostet ein Zeichen davon mehr als eines im Fliesstext (siehe
 * VORSPANN_BREITE).
 *
 * Und er steht nur da, wo er etwas sagt: hat der Tag genau einen Verfasser, ist
 * der Beitrag einfach der Text des Tages und bekommt keinen Vorspann (siehe
 * druckBeitraege). Auf zwölf von sechzehn Seiten denselben Namen zu drucken,
 * wäre keine Auskunft, sondern Möblierung.
 *
 * Die Zahlen unten sind an print.css geeicht, und zwar nachgemessen am
 * gerenderten Buch in Druckgeometrie (A5, 148 mm breit, Satzspiegel 128 mm),
 * nicht geschätzt. Wer in print.css die Kopfzone, die Karte oder die Fotoreihe
 * in der Höhe ändert, verschiebt damit auch diese Grenzen. Beide gehören
 * zusammen angefasst; overflow:hidden auf der Seite fängt den Fehler ab, aber
 * es schneidet dann Text weg, und das sieht niemand rechtzeitig.
 */

import type { DiaryPost } from '../../lib/types'
import type { PhotoSlot } from './imageFit'

/**
 * Die drei Vorlagen einer Etappenseite.
 *
 * bildstark   - wenig Text: ein grosses Bild übernimmt die Seite.
 * ausgewogen  - mittlerer Text: Text und zwei Bilder teilen sich den Platz.
 * textstark   - viel Text oder gar keine Bilder: der Text bekommt die Fläche.
 */
export type StageTemplate = 'bildstark' | 'ausgewogen' | 'textstark'

/* --- Gemessene Geometrie -------------------------------------------------- */

/**
 * Zeichen, die in eine Zeile des Tagebuchtexts passen.
 *
 * Gemessen und nicht gerechnet: die Textspalte ist 128 mm breit (483.3 px in
 * Druckgeometrie), .dr-text steht in 0.9 em, also 11.98 px, und eine Zeile ist
 * 17.37 px hoch. In dieses Mass wurden vier Textsorten gesetzt und die
 * entstandenen Zeilenkästen gezählt:
 *
 *     Reiseprosa                        80.0 Zeichen je Zeile
 *     lauter kurze Wörter              80.0
 *     Zahlen, Preise, Ortsnamen         77.0
 *     lange deutsche Komposita          73.0
 *
 * Hier steht 72 und nicht 80. Nicht der Mittelwert zählt, sondern die untere
 * Schranke: wer zu viele Zeichen je Zeile rechnet, rechnet zu wenige Zeilen und
 * schneidet den Rest ab. Ein Eintrag über Karawankentunnelmautstellen ist der
 * schlechteste Fall, und er ist kein erfundener.
 *
 * Die alte Fassung schätzte hier 62 - zu wenig, was den zu grossen
 * Zeilenansatz zufällig ausglich, solange jemand Fliesstext schrieb.
 */
export const ZEICHEN_JE_ZEILE = 72

/**
 * Was ein Zeichen des Vorspanns kostet, gemessen in Zeichen des Fliesstexts.
 *
 * Der Vorspann ist in Versalien gesetzt und gesperrt (.dr-vorspann in
 * print.css), ein Zeichen davon ist also breiter als eines im Fliesstext.
 * Gemessen am gerenderten Buch: die Zeile "JOSHUA HIRSIG — " ist 16 Zeichen
 * lang und belegt so viel Platz wie 21.4 Zeichen Fliesstext, "LEA — " belegt so
 * viel wie 7.8. Das ergibt Faktoren von 1.30 und 1.34.
 *
 * Hier steht 1.4 und nicht 1.34. Wie bei ZEICHEN_JE_ZEILE zählt die
 * ungünstige Seite: zu schmal gerechnet heisst zu wenige Zeilen und damit
 * abgeschnittener Text. Ein Vorspann ist höchstens zwei Dutzend Zeichen lang,
 * der Aufschlag kostet also nie mehr als ein paar Zeichen einer einzigen Zeile.
 */
export const VORSPANN_BREITE = 1.4

/**
 * Was hinter dem Namen steht, bevor der Text weiterläuft.
 *
 * Ein Gedankenstrich mit Abstand, so wie die Zeitschriftenzeile es macht. Er
 * gehört in die Rechnung und nicht nur ins Setzen - er belegt Platz in
 * derselben Zeile.
 */
export const VORSPANN_ANHANG = ' — '

/**
 * Wie viele Zeichen der Verfassername im Buch höchstens bekommt.
 *
 * Ein Anzeigename kommt aus dem Anmeldedienst und kann alles sein, bis hin zu
 * einer Mailadresse mit Firmennamen darin. Ohne Deckel schöbe ein einziger
 * langer Name eine ganze Zeile Tagebuch aus der Seite - derselbe Fehler wie bei
 * den ungedeckelten Einträgen der Planungsspalten, nur an anderer Stelle.
 * Gekürzt wird sichtbar, mit Auslassungspunkten.
 */
export const AUTOR_ZEICHEN_MAX = 24

/**
 * Was der Abstand zwischen zwei Beiträgen kostet, in Tagebuchzeilen.
 *
 * Gemessen: .dr-beitrag trägt 0.35 em Abstand nach unten (4.2 px bei 11.98 px
 * Schriftgrösse), also 0.24 Zeilen zu 17.37 px. Hier steht 0.3, wieder auf der
 * ungünstigen Seite gerundet.
 *
 * Der Abstand fällt je Zwischenraum an und nicht je Beitrag: ein einzelner
 * Beitrag kostet nichts, drei Beiträge kosten ihn zweimal.
 */
export const BEITRAG_ABSTAND_ZEILEN = 0.3

/**
 * Zeichen je Zeile in einer der beiden Planungsspalten.
 *
 * Dieselbe Messung in .dr-block (halbe Spalte, 226.3 px, 0.78 em): 41.8 für
 * Prosa, 44.4 für kurze Wörter, 37.3 für Ortsnamen mit Preisen, 36.5 für
 * Komposita. Also 36. Derselbe Wert deckt auch die kleinere Schrift der
 * Platznotiz und den Hinweiskasten ab - beide sind schmaler gesetzt, tragen
 * aber mehr Zeichen je Zeile, und zu wenig gerechnete Zeichen sind hier die
 * sichere Seite.
 */
export const ZEICHEN_JE_ZEILE_SPALTE = 36

/**
 * Was eine Zeile der Planungsspalte eine Tagebuchzeile kostet.
 *
 * 14.02 px zu 17.37 px. Die Spalten sind kleiner gesetzt als der Tagebuchtext,
 * ihre Zeilen sind also billiger - aber nicht umsonst, und genau diese
 * Umrechnung hat in der alten Fassung gefehlt.
 */
export const SPALTENZEILE = 0.81

/**
 * Zeilen, die dem Tagebuchtext auf einer Etappenseite blieben, wenn
 * Planungsspalten und Fotostreifen nichts kosteten.
 *
 * Gemessen: der Seitenkoerper ist 698 px hoch, davon gehen die Kopfzone mit
 * ihrem Abstand (75.9 px), der Kartenausschnitt (187 px) und die Zeile
 * "Tagebuch" darüber (23.7 px) immer ab. Bleiben 411.4 px, also 23.68 Zeilen
 * zu 17.37 px.
 *
 * Die Zahl ist an elf gerenderten Etappenseiten geprüft: für jede von ihnen
 * sagt GRUNDZEILEN minus Fotostreifen minus Planungsspalten genau die Zeilenzahl
 * voraus, die im Browser gemessen wurde.
 *
 * Die 75.9 px der Kopfzone gelten für eine einzeilige Kopfzeile; bricht sie um,
 * steht die zweite Zeile als last.kopf in der Rechnung (siehe kopfZeilen). Die
 * Kennzahlenleiste darunter ist dagegen in jedem Fall einzeilig - gemessen
 * 39.3 px, gleich ob mit Vergleich oder ohne und gleich, was im Feld land steht
 * (siehe LAND_ZEICHEN_EINZEILIG).
 */
export const GRUNDZEILEN = 23.6

/**
 * Was der Fotostreifen der Vorlage kostet, in Tagebuchzeilen.
 *
 * textstark  - 8.6 em hoch plus 0.6 em Abstand, also 122.5 px: 7.05 Zeilen.
 * ausgewogen - 12 em plus 0.6 em, also 167.7 px: 9.65 Zeilen.
 * bildstark  - der Streifen wächst hier mit dem übrigen Platz (flex:1), hat
 *              also keine feste Höhe. Die 11.5 Zeilen sind deshalb keine
 *              Messung, sondern eine Zusage: unter rund 200 px ist ein Bild,
 *              das die Seite tragen soll, keines mehr. Wer mehr Text hat,
 *              bekommt eine andere Vorlage statt kleinerer Bilder.
 */
export const FOTOZEILEN: Record<StageTemplate, number> = {
  bildstark: 11.5,
  ausgewogen: 9.7,
  textstark: 7.1,
}

/**
 * Was die Notizlinien der bildstarken Vorlage fest belegen.
 *
 * Dort ist das Tagebuch flex:none und die Linien sind auf 4.3 em gedeckelt
 * (57.2 px, also 3.3 Zeilen); sie stehen immer da, auch unter Text. Bei den
 * anderen Vorlagen nehmen sie sich nur, was uebrig bleibt, und kosten nichts.
 */
export const BILDSTARK_LINIEN_ZEILEN = 3.3

/**
 * Zeichen, nach denen die Kopfzeile umbricht.
 *
 * .dr-ziel ist 341.2 px breit und in 1.15 em gesetzt; gemessen passen dort je
 * nach Textsorte 36.5 bis 41.8 Zeichen. Ein Ziel "Von → Nach" darüber läuft
 * auf eine zweite Zeile und schiebt alles darunter nach unten.
 */
export const ZEICHEN_JE_ZEILE_KOPF = 36

/**
 * Wie viele Zeilen die Kopfzeile höchstens bekommt.
 *
 * Zwei, und das ist in print.css als line-clamp festgeschrieben. Ein Ortsname
 * kann beliebig lang sein - "Sankt Johann am Wimberg ob der Donau bei Bad
 * Leonfelden" ist keine Erfindung -, und ohne Deckel bricht die Kopfzeile auf
 * drei oder vier Zeilen um und drückt den Tagebuchtext von oben weg. Der
 * Deckel kürzt sichtbar mit Auslassungspunkten; der ganze Name steht auf der
 * Übersichtsseite und in der laufenden Kopfzeile der Fortsetzung.
 */
export const KOPF_ZEILEN_MAX = 2

/**
 * Wie viele Zeichen in der Landspalte der Kennzahlenleiste auf eine Zeile gehen.
 *
 * Zwölf, und die Zahl ist gerechnet und nicht geraten. Die Landspalte hat in
 * print.css einen Boden (--dr-land: minmax(7.3em, 1fr)): 7.3 em abzüglich
 * 0.5 em Polster sind 6.8 em für den Wert, der Wert steht in 0.92 em
 * Monospace, und eine Monospace-Zelle ist 0.6 em breit - also 0.552 em je
 * Zeichen und 6.62 em für zwölf davon.
 *
 * Gerechnet wird mit dem Boden und nicht mit der Breite, die die Spalte im
 * Regelfall bekommt (gemessen 99.1 px neben drei Vergleichszahlen, 165.7 px
 * ohne). Was darüber hinausgeht, ist Zugabe: die Etiketten stehen in einer
 * Systemschrift, und fällt die auf einem anderen Rechner breiter aus, nehmen
 * sich die Zahlenspalten mehr und die Textspalten bekommen weniger. Der Boden
 * ist genau die Zusicherung, dass diese zwölf Zeichen trotzdem bleiben - was
 * dann nachgibt, ist die Übernachtung.
 *
 * Ein Pfeil zählt doppelt. Im Feld land steht entweder ein Kürzel oder ein
 * Übergang - "HR→BA", "HU→AT→CH" -, und "→" kommt in den wenigsten
 * Monospace-Schriften vor; gesetzt wird es aus einer Ersatzschrift und ist dort
 * breiter als eine Zelle. Zwei Zellen decken das ab.
 *
 * Was länger ist, verschwindet nicht, sondern wird kleiner gesetzt und bricht
 * auf zwei Zeilen um - siehe .dr-land-lang in print.css. Die beiden kleinen
 * Zeilen sind zusammen so hoch wie eine normale, die Leiste bleibt also gleich
 * hoch und die vermessene Kopfzone gilt unverändert.
 */
export const LAND_ZEICHEN_EINZEILIG = 12

/**
 * Zeichen im Feld land, die breiter sind als eine Monospace-Zelle.
 *
 * Die Pfeile der Übergangsschreibweise, in den Formen, die auch metrics.ts
 * kennt. Die ASCII-Schreibweisen "->" und "=>" stehen nicht dabei: sie sind
 * schon zwei Zeichen und damit ohne Zutun doppelt gezählt.
 */
const LAND_BREITE_ZEICHEN = /[→←↔⇒➔]/g

/**
 * Ob ein Landeintrag für die Kennzahlenleiste zu lang ist und kleiner gesetzt
 * werden muss.
 *
 * Das Feld ist freier Text: die eine Reise trägt Kürzel ein ("SI", "HR→BA"),
 * die andere schreibt "Schweiz" aus, und beides ist richtig. Eine Kürzelpflicht
 * wäre die falsche Antwort - also entscheidet die Länge, wie gesetzt wird.
 *
 * Warum das hier steht und nicht in StagePage.tsx: es ist eine Aussage über die
 * Geometrie der Seite, und die steht in diesem Modul, damit gesetzt und
 * gerechnet wird, was zusammengehört. CSS kann diese Frage nicht beantworten -
 * es gibt keine Abfrage, die "wenn dieser Text breiter ist als seine Spalte"
 * ausdrückt.
 */
export function landZuLang(land: string): boolean {
  const text = land.trim()
  const pfeile = text.match(LAND_BREITE_ZEICHEN)?.length ?? 0

  return text.length + pfeile > LAND_ZEICHEN_EINZEILIG
}

/**
 * Was die Überschrift über dem Tulpenstreifen kostet, in Tagebuchzeilen.
 *
 * Gemessen: Streifen mit einer Reihe 103.8 px, die Reihe selbst 79.2 px, bleibt
 * 24.6 px für Zwischenzeile, Trennlinie und Abstand - 1.42 Zeilen. Sie fällt
 * einmal an und nicht je Reihe, deshalb ein eigener Posten.
 */
export const TULPENKOPF_ZEILEN = 1.5

/**
 * Was eine Tulpenreihe dem Tagebuchtext kostet, in Tagebuchzeilen.
 *
 * Gemessen: .dr-tulpenzeile ist mindestens 5.4 em hoch (71.9 px) und trägt
 * Polster und Trennlinie, zusammen 79.2 px - also 4.56 Zeilen.
 *
 * Die alte Fassung rechnete hier 460 Zeichen statt der geometrisch fälligen
 * 320, und der Aufschlag war ausdrücklich kein Sicherheitszuschlag auf den
 * Streifen, sondern eine Rücklage für die schwankende Höhe der
 * Planungsspalten. Die ist jetzt ausgerechnet statt geschätzt (planPlanung),
 * also fällt der Aufschlag weg: hier steht wieder die reine Geometrie.
 *
 * Ein Posten steht bewusst nicht in dieser Zahl: der Name des Zeichens, der
 * beim ersten Vorkommen einer Form neben ihr steht (.dr-tulpen-name in
 * print.css, erstesVorkommen in lib/tulip.ts). Er kostet die Reihe nichts - er
 * nimmt genau den Abstand ein, der ohne ihn über der Notiz steht. Gemessen am
 * gerenderten Buch: eine Reihe mit Namen und eine ohne sind beide 79.5 px hoch.
 * Wer diesen Abstand in print.css anfasst, macht daraus einen Posten, der hier
 * fehlt - und dann schneidet overflow:hidden wieder stumm Tagebuchtext weg.
 *
 * Dass die Reihe diese Höhe wirklich einhält, ist keine Annahme über die
 * Textmenge, sondern eine Zusicherung von print.css - siehe
 * TULPEN_NOTIZ_ZEILEN_MAX.
 */
export const TULPENREIHE_ZEILEN = 4.6

/**
 * Wie viele Zeilen die Notiz einer Tulpenreihe höchstens bekommt.
 *
 * Fünf, und das ist in print.css als line-clamp festgeschrieben - dieselbe
 * Bauart wie KOPF_ZEILEN_MAX bei der Kopfzeile. Die Zahl ist gemessen: die
 * Notizspalte ist 126.2 px breit, .dr-tulpen-notiz steht in 0.72 em (9.59 px)
 * auf 12.46 px Durchschuss und beginnt 8.6 px unter der Oberkante. Fünf Zeilen
 * sind damit 70.9 px, und die Reihe ist mindestens 71.9 px hoch: die fünfte
 * Zeile ist die letzte, die noch hineinpasst, ohne die Reihe wachsen zu lassen.
 *
 * Warum ein Deckel in Zeilen und nicht in Zeichen: eine Reihe wächst mit den
 * Zeilen und nicht mit den Zeichen, und beides läuft weit auseinander.
 * Gemessen am gerenderten Buch belegen dieselben 130 Zeichen fünf Zeilen als
 * Fliesstext, sechs als Notiz mit Zahlen und Ortsnamen und acht in Versalien.
 * Ein Deckel in Zeichen, der auch den ungünstigsten Fall sicher abdeckt, läge
 * bei rund 50 Zeichen und nähme jeder gewöhnlichen Notiz die Hälfte weg; ein
 * grosszügiger Deckel liesse den ungünstigen Fall weiterhin überlaufen. Der
 * line-clamp misst dagegen das, worauf es ankommt, und tut es beim Setzen.
 *
 * Was er wegnimmt, nimmt er sichtbar weg: der Browser setzt Auslassungspunkte
 * ans Ende der letzten Zeile. Vorher wuchs die Reihe stattdessen, und weil
 * .dr-seite--roadbook .dr-tulpen bei acht Reihen overflow:hidden trägt, fielen
 * die untersten Reihen einer vollen Roadbookseite ersatzlos unter die Kante -
 * ganze Fahranweisungen, ohne ein Zeichen auf dem Papier, das davon erzählt.
 *
 * Der Deckel gegen zu lange Notizen steht ausserdem schon im Eingabefeld, siehe
 * TULPEN_NOTIZ_ZEICHEN_MAX in lib/tulip.ts. Er verhindert die überlange Notiz
 * beim Schreiben; diese Zahl hier hält die Geometrie auch für alles, was schon
 * im Bestand steht.
 */
export const TULPEN_NOTIZ_ZEILEN_MAX = 5

/**
 * Der Deckel gilt unverändert auch dort, wo der Name des Zeichens über der
 * Notiz steht - und das ist gemessen und nicht angenommen.
 *
 * Der Name ist ein eigener Block im Kasten und kein Zeilenkasten der Notiz; der
 * line-clamp zählt ihn nicht mit. Die Reihe bleibt trotzdem gleich hoch, denn
 * er nimmt genau den Abstand ein, den er verdrängt (siehe TULPENREIHE_ZEILEN
 * und .dr-tulpen-notiz--benannt in print.css): 8.63 px Namenszeile für 8.6 px
 * Polster.
 *
 * Ein Anlauf mit einer Zeile mehr für die benannte Reihe ist an der Messung
 * gescheitert - die Reihe wuchs auf 90.9 px. Zählte ein Browser den Namen doch
 * als Zeile, bekäme die Notiz dort eine Zeile weniger; das ist die
 * ungefährliche Richtung, und deshalb steht hier eine Zahl und nicht zwei.
 */

/**
 * Wie viele Tulpen in einer Zeile nebeneinander stehen.
 *
 * Zwei. Die Rechnung: der Satzspiegel ist 128 mm breit, eine Tulpe 19 mm; mit
 * Kilometerstand und Hinweis ist ein Eintrag rund 62 mm breit. Drei
 * nebeneinander liessen dem Hinweis zwölf Zeichen je Zeile - das ist keine
 * Notiz mehr, sondern eine Abkürzung, die im Auto niemand auflöst.
 */
export const TULPEN_SPALTEN = 2

/**
 * Wie viele Tulpen höchstens auf die Etappenseite selbst kommen.
 *
 * Vier wären zwei Reihen und damit rund elf Zeilen. Die Zahl steht hier als
 * Obergrenze und nicht als Versprechen: was davon wirklich Platz hat, rechnet
 * inlineTulips aus der Kapazität der Vorlage aus.
 */
export const MAX_TULPEN_ERSTE_SEITE = 4

/**
 * Tulpen je eigener Roadbookseite.
 *
 * Acht Reihen zu zwei Spalten. In der Höhe bleiben unter der laufenden
 * Kopfzeile rund 168 mm, also acht Reihen zu 19 mm mit Luft dazwischen.
 */
export const ROADBOOK_TULPEN = TULPEN_SPALTEN * 8

/**
 * Zeilen je Fortsetzungsseite.
 *
 * Deutlich mehr als auf der ersten Seite: dort gehen Kopfzone, Karte,
 * Planungsspalten und Fotostreifen ab, die Fortsetzung hat nur eine schmale
 * Kopfzeile und darunter die ganze Seite. Gemessen bleiben 673 px, also 38
 * Zeilen; hier steht 37, damit die letzte Zeile nicht auf der Kante steht.
 */
export const FORTSETZUNG_ZEILEN = 37

/**
 * Was dem Tagebuchtext auf der ersten Seite mindestens bleiben muss.
 *
 * Drei Zeilen. Weniger wäre ein Absatz, der nach zwei Sätzen auf eine
 * Fortsetzungsseite umbricht - dann steht der Tag auf zwei Seiten, ohne dass
 * eine davon voll ist.
 */
export const TEXT_MINDESTENS = 3

/** Höchstzahl an Fortsetzungsseiten je Etappe, als Reissleine. */
export const MAX_FORTSETZUNGEN = 8

/**
 * Zeilen, die auf der letzten Seite für den Hinweis freibleiben.
 *
 * Wenn die Reissleine gezogen wird, steht unter dem Text der Satz, dass hier
 * etwas fehlt. Ohne diese Rücklage stünde er auf einer randvollen Seite und
 * wäre das erste, was overflow:hidden wegschneidet - ein Hinweis, den man
 * nicht sieht, ist keiner.
 */
export const REST_HINWEIS_ZEILEN = 2

/* --- Die Planungsspalten -------------------------------------------------- */

/**
 * Wie viele Einträge einer Liste auf der Etappenseite stehen.
 *
 * Eine Grenze muss sein: die Seite ist 210 mm hoch, und was darüber hinausgeht,
 * schneidet overflow:hidden weg - stillschweigend. Lieber eine sichtbare
 * Verkürzung mit dem Hinweis, wie viel fehlt, als eine Liste, die mitten im
 * Wort aufhört. Die Werte sind so gewählt, dass die recherchierte
 * Startbestückung vollständig durchkommt.
 *
 * Sie stehen hier und nicht in StagePage.tsx, weil die Seitenplanung mit
 * denselben Zahlen rechnen muss, mit denen gesetzt wird. Getrennte Konstanten
 * waren genau der Fehler: die Vorlage rechnete stillschweigend mit kurzen
 * Einträgen, und das stand nirgends und wurde nirgends erzwungen.
 */
export const MAX_HIGHLIGHTS = 5
export const MAX_CAMPING = 2
export const MAX_ABENDS = 3

/**
 * Wie viele Spaltenzeilen ein einzelner Eintrag höchstens bekommt.
 *
 * Drei, also rund 108 Zeichen. Die Anzahl der Einträge war gedeckelt, ihre
 * Länge nicht - ein einziges Highlight von 300 Zeichen sind neun Zeilen in der
 * Halbspalte und schieben den Tagebuchtext von unten aus der Seite. Was länger
 * ist, wird an der Wortgrenze gekürzt und bekommt Auslassungspunkte: auf dem
 * Papier steht dann, dass hier etwas fehlt.
 */
export const EINTRAG_ZEILEN_MAX = 3

/**
 * Wie hoch die Planungsspalten höchstens werden, in Spaltenzeilen.
 *
 * Sechzehn, also rund 13 Tagebuchzeilen. Die Rechnung dahinter: von den 23.6
 * Grundzeilen müssen der Fotostreifen der textstarken Vorlage (7.1) und das
 * Mindestmass des Tagebuchtexts (3) uebrig bleiben. Was darüber hinausginge,
 * schöbe den Text aus der Seite, statt ihn auf eine Fortsetzung zu schicken.
 *
 * In der Praxis greift der Deckel nie: die grösste gemessene Spalte der
 * recherchierten Reise ist 145 px hoch, also gut zehn Spaltenzeilen. Er ist die
 * Zusicherung, dass die Rechnung darüber nicht überholt werden kann.
 */
export const PLANUNG_ZEILEN_MAX = 16

/** Ein Platz zum Übernachten, so wie er im Buch steht. */
export interface CampingEintrag {
  name: string
  price?: string
  note?: string
}

/** Was auf der Etappenseite links und rechts unter der Karte steht. */
export interface PlanungInput {
  highlights: readonly string[]
  night: readonly string[]
  camping: readonly CampingEintrag[]
  tip: string
}

/** Die Planungsspalten, gekürzt auf das, was wirklich Platz hat. */
export interface PlanungPlan {
  highlights: string[]
  /** Wie viele Highlights nicht abgedruckt sind. */
  highlightsRest: number
  night: string[]
  nightRest: number
  camping: CampingEintrag[]
  campingRest: number
  /** Der Tipp, gekürzt. Leer, wenn keiner da ist oder keiner mehr passt. */
  tip: string
  /** Ob der Tipp ganz weggefallen ist, obwohl einer da war. */
  tipWeg: boolean
  /** Höhe der höheren Spalte, in Tagebuchzeilen. */
  zeilen: number
}

/** Eine leere Planung, für Etappen ohne Angaben und Aufrufer ohne Daten. */
export const LEERE_PLANUNG: PlanungInput = {
  highlights: [],
  night: [],
  camping: [],
  tip: '',
}

/**
 * Wie viele Zeilen ein Text belegt.
 *
 * Die eine Rechnung, um die sich dieses Modul dreht: ein "\n" beendet eine
 * Zeile, und ein Abschnitt, der länger ist als eine Zeile breit, belegt so
 * viele, wie er braucht. Leerer Text belegt keine - nicht eine leere.
 */
export function textZeilen(text: string, jeZeile: number = ZEICHEN_JE_ZEILE): number {
  if (!text) return 0
  const breite = Math.max(1, Math.floor(jeZeile))
  let zeilen = 0
  for (const abschnitt of text.split('\n')) {
    zeilen += Math.max(1, Math.ceil(abschnitt.length / breite))
  }
  return zeilen
}

/**
 * Kürzt einen Text auf so viele Zeilen und setzt Auslassungspunkte.
 *
 * Geschnitten wird am letzten Wortende davor. Die Auslassungspunkte sind kein
 * Schmuck, sondern die ganze Aussage: ein Eintrag, der ohne sie endet, sieht
 * auf Papier aus wie ein vollständiger.
 */
export function kuerzeAufZeilen(text: string, zeilen: number, jeZeile: number): string {
  const ganz = text.trim()
  const grenze = Math.max(1, Math.floor(zeilen)) * Math.max(1, Math.floor(jeZeile))
  if (textZeilen(ganz, jeZeile) <= Math.max(1, Math.floor(zeilen))) return ganz

  // Ein Zeichen Luft für die Auslassungspunkte selbst.
  const schnitt = wortgrenze(ganz, grenze - 1)
  return `${ganz.slice(0, schnitt).trimEnd()}…`
}

/* --- Die Beiträge -------------------------------------------------------- */

/**
 * Ein Beitrag, so wie er in den Druck geht.
 *
 * Nur zwei Felder, und das ist Absicht: alles, was den Beitrag sonst ausmacht -
 * wer ihn wann geschrieben hat, wer ihn ändern darf -, ist für die Frage, wie
 * viel Platz er braucht, ohne Belang. Was hier ankommt, ist die Entscheidung,
 * die druckBeitraege getroffen hat.
 */
export interface DruckBeitrag {
  /**
   * Der Verfassername, der vor dem Text in der Zeile steht. Leer heisst: kein
   * Vorspann - und das ist der Normalfall, siehe druckBeitraege.
   */
  vorspann: string
  text: string
}

/**
 * Was der Vorspann an Zeichen des Fliesstexts belegt. Ohne Vorspann nichts.
 *
 * Der Name, der Gedankenstrich und der Abstand dahinter, mal dem Aufschlag für
 * die gesperrten Versalien. Aufgerundet: eine halbe Stelle Zeichen gibt es
 * nicht, und die volle ist die sichere Seite.
 */
export function vorspannZeichen(vorspann: string): number {
  if (!vorspann) return 0
  return Math.ceil((vorspann.length + VORSPANN_ANHANG.length) * VORSPANN_BREITE)
}

/**
 * Der Beitrag als eine Zeichenkette, in der der Vorspann so viel Platz belegt
 * wie im Satz.
 *
 * Der Umweg über eine Ersatzzeichenkette statt über eine zweite Rechnung ist
 * der Punkt: so gilt für den Vorspann dieselbe Zeilenzählung wie für alles
 * andere - textZeilen und schnittFuerZeilen brauchen nichts von ihm zu wissen,
 * und es gibt keine zweite Stelle, an der sich ein Fehler verstecken kann. Das
 * Leerzeichen am Ende steht da, damit ein Schnitt an der Wortgrenze auch
 * unmittelbar hinter dem Vorspann fallen kann.
 */
function messtext(beitrag: DruckBeitrag): string {
  const zeichen = vorspannZeichen(beitrag.vorspann)
  if (zeichen === 0) return beitrag.text
  return `${'–'.repeat(zeichen - 1)} ${beitrag.text}`
}

/** Was ein einzelner Beitrag an Zeilen belegt, mit seinem Vorspann. */
export function beitragZeilen(beitrag: DruckBeitrag): number {
  return textZeilen(messtext(beitrag))
}

/**
 * Was alle Beiträge einer Etappe zusammen an Zeilen belegen.
 *
 * Die Abstände zählen mit, aber nur die zwischen zwei Beiträgen. Diese Zahl ist
 * die Grundlage für die Wahl der Vorlage und für die Frage, ob der
 * Tulpenstreifen noch auf die Etappenseite passt.
 */
export function beitraegeZeilen(beitraege: readonly DruckBeitrag[]): number {
  if (beitraege.length === 0) return 0
  let zeilen = (beitraege.length - 1) * BEITRAG_ABSTAND_ZEILEN
  for (const beitrag of beitraege) zeilen += beitragZeilen(beitrag)
  return zeilen
}

/**
 * Macht aus den Beiträgen einer Etappe das, was gedruckt wird - und entscheidet
 * dabei, ob ein Vorspann gesetzt wird.
 *
 * Die Regel: der Vorspann steht genau dann, wenn an diesem Tag mehr als eine
 * Person geschrieben hat. Sonst nicht.
 *
 * Der Normalfall einer Reise zu zweit ist, dass abends nur einer Lust hat. Dann
 * ist sein Beitrag einfach der Text des Tages, und ein Name darüber wäre keine
 * Auskunft, sondern eine Behörde: auf zwölf von sechzehn Seiten stünde
 * dasselbe. Wer geschrieben hat, wird erst dann zur Information, wenn es mehr
 * als einer war - dann trägt jeder Beitrag seinen Vorspann, auch der erste,
 * denn sonst wäre der erste der namenlose Grundtext und alle weiteren
 * Einwürfe.
 *
 * Verglichen werden die Kennungen und nicht die Namen: derselbe Mensch, der
 * seinen Anzeigenamen zwischendurch geändert hat, ist eine Person und keine
 * zwei. Ein Beitrag ohne Kennung - aus einer alten Datei - zählt als eigener
 * Verfasser; mehr lässt sich über ihn nicht sagen.
 *
 * Beiträge ohne Text fallen weg. Eine Verfasserzeile über nichts ist im Buch
 * eine Zeile, die eine Aussage verspricht und keine hat.
 */
export function druckBeitraege(posts: readonly DiaryPost[]): DruckBeitrag[] {
  const echte = posts.filter((post) => post.text.trim() !== '')
  const verfasser = new Set(echte.map((post) => post.authorId))
  const mitVorspann = verfasser.size > 1

  return echte.map((post) => ({
    vorspann: mitVorspann ? vorspannFuer(post.authorName) : '',
    text: post.text,
  }))
}

/**
 * Der Name im Vorspann, gekürzt.
 *
 * Ohne Namen steht "Unbekannt" da und nicht nichts. Sobald an einem Tag zwei
 * Leute geschrieben haben, trägt jeder Beitrag seinen Vorspann - ein Beitrag
 * ohne wäre keine Bescheidenheit, sondern sähe aus wie die Fortsetzung des
 * vorigen.
 */
function vorspannFuer(name: string): string {
  return kuerzeAufZeilen(name, 1, AUTOR_ZEICHEN_MAX) || 'Unbekannt'
}

/** Was ein Listeneintrag an Spaltenzeilen kostet, samt Abstand darunter. */
function eintragZeilen(text: string): number {
  return textZeilen(text, ZEICHEN_JE_ZEILE_SPALTE) + 0.12
}

/** Was ein Übernachtungsplatz an Spaltenzeilen kostet. */
function platzZeilen(platz: CampingEintrag): number {
  const kopf = `${platz.name} ${platz.price ?? ''}`.trim()
  return eintragZeilen(kopf) + (platz.note ? textZeilen(platz.note, ZEICHEN_JE_ZEILE_SPALTE) : 0)
}

/**
 * Kürzt die Planungsspalten auf das, was auf die Seite passt, und sagt, wie
 * hoch sie danach sind.
 *
 * Das ist die Antwort auf den zweiten Fehler dieses Moduls: die Vorlage war auf
 * kurze Einträge geeicht, ohne dass das irgendwo stand oder erzwungen wurde.
 * Beides wird hier nachgeholt. Die Höhe wird ausgerechnet - die Seitenplanung
 * weiss also, was die Spalten wirklich kosten - und sie ist gedeckelt, also kann
 * kein einzelner Eintrag die Rechnung überholen.
 *
 * Gekürzt wird in dieser Reihenfolge: erst die Anzahl je Liste (MAX_*), dann
 * die Länge des einzelnen Eintrags (EINTRAG_ZEILEN_MAX), dann - und nur wenn es
 * dann noch immer nicht reicht - fallen Einträge von hinten weg. Jede dieser
 * drei Kürzungen ist auf dem Papier sichtbar: als Auslassungspunkte oder als
 * Zeile "und N weitere".
 *
 * Die beiden Spalten stehen nebeneinander, also zählt die höhere.
 */
export function planPlanung(input: PlanungInput): PlanungPlan {
  const kurz = (t: string) => kuerzeAufZeilen(t, EINTRAG_ZEILEN_MAX, ZEICHEN_JE_ZEILE_SPALTE)

  const highlights = input.highlights.slice(0, MAX_HIGHLIGHTS).map(kurz)
  const night = input.night.slice(0, MAX_ABENDS).map(kurz)
  const camping = input.camping.slice(0, MAX_CAMPING).map((platz) => ({
    name: kurz(platz.name),
    price: platz.price,
    note: platz.note ? kurz(platz.note) : platz.note,
  }))
  let tip = input.tip ? kurz(input.tip) : ''

  let highlightsRest = Math.max(0, input.highlights.length - highlights.length)
  let nightRest = Math.max(0, input.night.length - night.length)
  let campingRest = Math.max(0, input.camping.length - camping.length)
  let tipWeg = false

  /* Links: "Unterwegs" mit den Highlights, darunter "Abends". */
  const linkeZeilen = () => {
    let zeilen = 1 + (highlightsRest > 0 ? 1.12 : 0)
    if (highlights.length === 0) zeilen += 1
    for (const eintrag of highlights) zeilen += eintragZeilen(eintrag)
    if (night.length > 0) {
      zeilen += 1.3 + (nightRest > 0 ? 1.12 : 0)
      for (const eintrag of night) zeilen += eintragZeilen(eintrag)
    }
    return zeilen
  }

  /* Rechts: "Schlafen" mit den Plätzen, darunter der Hinweiskasten. */
  const rechteZeilen = () => {
    let zeilen = 1 + (campingRest > 0 ? 1.12 : 0)
    if (camping.length === 0) zeilen += 1
    for (const platz of camping) zeilen += platzZeilen(platz)
    if (tip) zeilen += textZeilen(tip, ZEICHEN_JE_ZEILE_SPALTE) + 0.6
    return zeilen
  }

  const hoch = () => Math.max(linkeZeilen(), rechteZeilen())

  /*
   * Wenn selbst die gekürzten Einträge nicht passen, fallen sie weg - von
   * hinten, und nur aus der Spalte, die zu hoch ist. Der Tipp geht zuerst: er
   * ist der einzige Eintrag ohne eigene Zeile "und N weitere", und ein Hinweis,
   * den man nicht ganz lesen kann, ist weniger wert als ein Schlafplatz.
   */
  let sicherung = 0
  while (hoch() > PLANUNG_ZEILEN_MAX && sicherung++ < 40) {
    if (rechteZeilen() >= linkeZeilen()) {
      if (tip) {
        tip = ''
        tipWeg = true
      } else if (camping.length > 0) {
        camping.pop()
        campingRest++
      } else break
    } else if (night.length > 0) {
      night.pop()
      nightRest++
    } else if (highlights.length > 1) {
      highlights.pop()
      highlightsRest++
    } else break
  }

  return {
    highlights,
    highlightsRest,
    night,
    nightRest,
    camping,
    campingRest,
    tip,
    tipWeg,
    zeilen: Math.min(hoch(), PLANUNG_ZEILEN_MAX) * SPALTENZEILE,
  }
}

/**
 * Was die Kopfzeile über eine Zeile hinaus kostet.
 *
 * Null oder eins: bis ZEICHEN_JE_ZEILE_KOPF steht "Von → Nach" auf einer Zeile,
 * darüber auf zweien, und mehr als zwei lässt print.css nicht zu.
 */
export function kopfZeilen(from: string, to: string): number {
  const ziel = `${from} → ${to}`.trim()
  return Math.min(textZeilen(ziel, ZEICHEN_JE_ZEILE_KOPF), KOPF_ZEILEN_MAX) - 1
}

/* --- Die Seite ------------------------------------------------------------ */

/** Fotoplätze der ersten Seite, je Vorlage, in der Reihenfolge des Layouts. */
export const VORLAGE_PLAETZE: Record<StageTemplate, PhotoSlot[]> = {
  bildstark: ['held', 'halb', 'halb'],
  ausgewogen: ['halb', 'halb'],
  textstark: ['viertel', 'viertel', 'viertel'],
}

/** Fotos je reiner Bildseite. Drei Zweierreihen, mit Luft nach unten. */
export const BILDSEITE_PLAETZE = 6

/**
 * Was auf der Etappenseite ausser dem Tagebuchtext Platz braucht.
 *
 * Alles davon war früher entweder eine feste Annahme (die Planungsspalten) oder
 * gar nicht bedacht (die umbrechende Kopfzeile). Jetzt steht es als Rechnung da,
 * und die Seitenkapazität folgt daraus.
 */
export interface SeitenLast {
  /** Höhe der Planungsspalten, in Tagebuchzeilen. */
  planung: number
  /** Zusatzzeilen der Kopfzeile, wenn der Ortsname umbricht. */
  kopf: number
  /** Wie viele Tulpen auf der ersten Seite stehen. */
  tulpen: number
  /** Ob die Etappe Fotos hat. Ohne Fotos entfällt der Streifen ganz. */
  fotos: boolean
}

/** Eine Last ohne alles: für Aufrufer, die nur die Vorlage wissen wollen. */
export const KEINE_LAST: SeitenLast = { planung: 0, kopf: 0, tulpen: 0, fotos: true }

/** Was ein Streifen aus so vielen Tulpen dem Text wegnimmt. Ohne Tulpen nichts. */
function tulpenAbzug(tulpen: number): number {
  const anzahl = Math.max(0, Math.floor(tulpen))
  if (anzahl === 0) return 0

  const reihen = Math.ceil(anzahl / TULPEN_SPALTEN)
  return TULPENKOPF_ZEILEN + reihen * TULPENREIHE_ZEILEN
}

/**
 * Wie viele Zeilen der Tagebuchtext auf der ersten Seite bekommt.
 *
 * Von den Grundzeilen gehen ab: der Fotostreifen der Vorlage, die
 * Planungsspalten, eine umgebrochene Kopfzeile, der Tulpenstreifen - und bei der
 * bildstarken Vorlage zusätzlich ihre festen Notizlinien.
 *
 * Das Ergebnis ist nie negativ. Null Zeilen heisst: auf dieser Seite ist für
 * den Eintrag kein Platz, er fängt auf der Fortsetzung an. Das ist eine
 * gültige Antwort und kein Fehler.
 */
export function seitenKapazitaet(template: StageTemplate, last: SeitenLast): number {
  const fotos = last.fotos ? FOTOZEILEN[template] : 0
  const linien = template === 'bildstark' ? BILDSTARK_LINIEN_ZEILEN : 0

  const frei =
    GRUNDZEILEN -
    fotos -
    linien -
    Math.max(0, last.planung) -
    Math.max(0, last.kopf) -
    tulpenAbzug(last.tulpen)

  return Math.max(0, Math.floor(frei))
}

/**
 * Wie viele Tulpen auf die Etappenseite selbst passen.
 *
 * Zwei Bedingungen, und die zweite ist die wichtigere.
 *
 * Erstens muss dem Tagebuchtext sein Mindestmass bleiben. Zweitens - und das
 * ist die Regel, die diese Seite überhaupt aufgehen lässt - kommt der Streifen
 * nur dann auf die Etappenseite, wenn der ganze Eintrag des Tages daneben noch
 * Platz hat. Sobald er das nicht mehr tut, wandert der Streifen auf seine eigene
 * Roadbookseite und der Text behält die ganze Seite.
 *
 * Die Alternative wäre gewesen, den Text am Streifen vorbei auf eine
 * Fortsetzung zu drücken. Das ergibt eine Etappenseite mit zwei Zeilen Tagebuch
 * und dem Rest eine Seite weiter - und weil danach fast jede Etappe mit Tulpen
 * eine Fortsetzung braucht, ein deutlich dickeres Buch für ein schlechteres.
 * So bleibt beides ganz: der Streifen und der Text.
 *
 * Nebenwirkung, und eine erwünschte: solange unterwegs noch nichts
 * aufgeschrieben ist, steht der Streifen auf der Etappenseite - also genau
 * dann, wenn er gebraucht wird. Im fertigen Buch, wenn der Abend lang geworden
 * ist, hat er seine eigene Seite.
 *
 * Was nicht auf die erste Seite kommt, ist nicht verloren, sondern bekommt eine
 * Roadbookseite - anders als beim Text gibt es hier nichts abzuschneiden.
 */
export function inlineTulips(
  count: number,
  template: StageTemplate,
  zeilen = 0,
  last: SeitenLast = KEINE_LAST,
): number {
  let tulpen = Math.min(Math.max(0, Math.floor(count)), MAX_TULPEN_ERSTE_SEITE)

  while (tulpen > 0) {
    const platz = seitenKapazitaet(template, { ...last, tulpen })
    if (platz >= TEXT_MINDESTENS && Math.max(0, zeilen) <= platz) break
    tulpen--
  }
  return tulpen
}

/** Tulpen einer Etappe, aufgeteilt auf erste Seite und eigene Roadbookseiten. */
export interface TulipPlan {
  /** Stellen in der Tulpenliste der Etappe, von 0 an. */
  first: number[]
  /** Je Eintrag eine zusätzliche Seite mit lauter Tulpenzeilen. */
  extraPages: number[][]
}

/**
 * Verteilt die Tulpen auf die erste Seite und die Roadbookseiten dahinter.
 *
 * Dieselbe Aufteilung wie bei den Fotos, aus demselben Grund: was auf die
 * Etappenseite passt, steht dort, und der Rest bekommt Seiten, deren
 * Textkapazität nicht davon abhängt, wie viel zufällig uebrig war.
 */
export function placeTulips(
  count: number,
  template: StageTemplate,
  zeilen = 0,
  last: SeitenLast = KEINE_LAST,
): TulipPlan {
  const gesamt = Math.max(0, Math.floor(count))
  const auf1 = inlineTulips(gesamt, template, zeilen, last)

  const first: number[] = []
  for (let i = 0; i < auf1; i++) first.push(i)

  const extraPages: number[][] = []
  for (let i = auf1; i < gesamt; i += ROADBOOK_TULPEN) {
    const seite: number[] = []
    for (let j = i; j < gesamt && j < i + ROADBOOK_TULPEN; j++) seite.push(j)
    extraPages.push(seite)
  }

  return { first, extraPages }
}

/** Ein Foto mit dem Platz, den es auf der Seite bekommt. */
export interface PlacedPhoto {
  /** Stelle in der Fotoliste der Etappe, von 0 an. */
  index: number
  slot: PhotoSlot
}

/** Was auf eine einzelne Seite kommt. */
export interface StagePagePlan {
  /**
   * Die Beiträge oder Beitragsteile, die auf diese Seite gehören.
   *
   * Eine Liste und keine Zeichenkette: der Wechsel von einem Beitrag zum
   * nächsten wird gesetzt (Vorspann, Abstand) und ist damit nichts, was sich
   * aus einem zusammengeklebten Text noch zurückgewinnen liesse.
   */
  beitraege: DruckBeitrag[]
  photos: PlacedPhoto[]
  /** Stellen in der Tulpenliste der Etappe, die auf dieser Seite stehen. */
  tulips: number[]
  /**
   * Zeichen des Eintrags, die auch nach der letzten Fortsetzung keinen Platz
   * mehr haben. Steht nur auf der Seite, auf der der Text abbricht, und nur
   * dann, wenn die Reissleine wirklich gezogen wurde.
   */
  ungedruckt?: number
}

/** Der vollständige Plan einer Etappe: erste Seite und was überläuft. */
export interface StagePlan {
  template: StageTemplate
  first: StagePagePlan
  /** Fortsetzungsseiten, leer wenn alles auf eine Seite passt. */
  continuations: StagePagePlan[]
  /** Die Planungsspalten, gekürzt - so gesetzt, wie hier gerechnet. */
  planung: PlanungPlan
}

/**
 * Wählt die Vorlage aus Textmenge, Fotoanzahl und dem, was die Seite sonst
 * schon trägt.
 *
 * Ohne Fotos ist die Frage entschieden, egal wie kurz der Text ist: eine
 * bildstarke Vorlage ohne Bild wäre eine grosse leere Fläche. Der Platz geht
 * dann an den Text und an die Notizlinien darunter - eine Etappe ohne Bild und
 * ohne Eintrag ergibt so eine Seite zum Vollschreiben, nicht eine Lücke.
 *
 * Die Schwellen sind keine eigenen Zahlen mehr, sondern die Kapazitäten der
 * Vorlagen selbst: eine Vorlage kommt in Frage, wenn der Eintrag auf ihr ganz
 * Platz hat. Früher standen dort zwei feste Zeichenzahlen, die weder von den
 * Planungsspalten noch von der Kopfzeile etwas wussten - eine Etappe mit vollen
 * Spalten bekam dieselbe Vorlage wie eine mit leeren, und nur eine der drei
 * Zahlen war überhaupt gemessen.
 */
export function chooseTemplate(
  zeilen: number,
  photoCount: number,
  tulipCount = 0,
  last: SeitenLast = KEINE_LAST,
): StageTemplate {
  if (photoCount <= 0) return 'textstark'

  /*
   * Eine Etappe mit Tulpen bekommt immer die textstarke Vorlage.
   *
   * Ihr Fotostreifen ist der niedrige: die ausgewogene Vorlage gibt ihm 12 em,
   * also 42 mm, die textstarke 8.6 em, also 30 mm. Diese zwölf Millimeter sind
   * mehr als die Hälfte dessen, was eine Tulpenreihe braucht - genau der Platz,
   * den der Streifen sonst dem Text wegnimmt.
   */
  if (tulipCount > 0) return 'textstark'

  const passt = (vorlage: StageTemplate) =>
    Math.max(0, zeilen) <= seitenKapazitaet(vorlage, { ...last, fotos: true, tulpen: 0 })

  if (passt('bildstark')) return 'bildstark'
  if (passt('ausgewogen')) return 'ausgewogen'
  return 'textstark'
}

/** Fotos einer Etappe, aufgeteilt auf erste Seite und reine Bildseiten. */
export interface PhotoPlan {
  first: PlacedPhoto[]
  /** Je Eintrag eine zusätzliche Seite, die nur Bilder trägt. */
  extraPages: PlacedPhoto[][]
}

/**
 * Verteilt die Fotos auf die Plätze der Vorlage.
 *
 * Was nicht auf die erste Seite passt, bekommt eigene Bildseiten mit lauter
 * halben Plätzen. Diese Seiten stehen bewusst nicht gemischt mit Text: eine
 * Fortsetzung ist entweder Text oder Bild. Sonst hängt die Textkapazität einer
 * Seite davon ab, wie viele Bilder zufällig noch uebrig waren, und dieselbe
 * Etappe sähe nach dem Hinzufügen eines Fotos ganz anders aus.
 */
export function placePhotos(count: number, template: StageTemplate): PhotoPlan {
  const gesamt = Math.max(0, Math.floor(count))
  const plaetze = VORLAGE_PLAETZE[template]

  const first: PlacedPhoto[] = []
  for (let i = 0; i < gesamt && i < plaetze.length; i++) {
    first.push({ index: i, slot: plaetze[i]! })
  }

  const extraPages: PlacedPhoto[][] = []
  for (let i = first.length; i < gesamt; i += BILDSEITE_PLAETZE) {
    const seite: PlacedPhoto[] = []
    for (let j = i; j < gesamt && j < i + BILDSEITE_PLAETZE; j++) {
      seite.push({ index: j, slot: 'halb' })
    }
    extraPages.push(seite)
  }

  return { first, extraPages }
}

/** Der Tagebuchtext, aufgeteilt auf Seiten. */
export interface TextPlan {
  /** Ein Stück je Seite. */
  stuecke: string[]
  /**
   * Zeichen, die auch nach der letzten Fortsetzung nicht mehr untergebracht
   * sind. Üblicherweise null; alles darüber gehört auf dem Papier gesagt.
   */
  ungedruckt: number
}

/** Die Beiträge, aufgeteilt auf Seiten. */
export interface BeitragPlan {
  /** Je Seite die Beiträge oder Beitragsteile, die auf sie kommen. */
  seiten: DruckBeitrag[][]
  /**
   * Zeichen, die auch nach der letzten Fortsetzung nicht mehr untergebracht
   * sind. Üblicherweise null; alles darüber gehört auf dem Papier gesagt.
   */
  ungedruckt: number
}

/**
 * Verteilt die Beiträge auf Seiten, die je ihr Zeilenbudget einhalten.
 *
 * Gemessen wird in Zeilen: ein "\n" beendet eine, ein zu langer Abschnitt
 * belegt mehrere, ein Vorspann belegt Zeichen in seiner ersten (siehe
 * messtext). Was auf eine Seite passt, kommt darauf; was nicht mehr ganz passt,
 * wird geschnitten, und der Rest fängt auf der nächsten Seite an - ohne
 * Vorspann, denn dort ist er nicht der Anfang eines Beitrags, sondern die
 * Fortsetzung eines angefangenen Satzes.
 *
 * Geschnitten wird am letzten Wortende vor der Grenze. Findet sich keines - ein
 * einzelnes sehr langes Wort, etwa eine Adresse ohne Leerzeichen - wird hart
 * getrennt: ein Stück, das länger ist als die Seite, würde von overflow:hidden
 * abgeschnitten, und das fällt niemandem auf.
 *
 * Die Reissleine bei MAX_FORTSETZUNGEN bleibt, und sie ist nicht stumm: was
 * dann noch übrig ist, steht als Zahl in ungedruckt und als Satz auf dem
 * Papier.
 *
 * @param ersteZeilen Zeilen für die erste Seite.
 * @param folgeZeilen Zeilen für jede weitere.
 * @returns Je Seite eine Liste. Ohne Text ergibt das eine leere Liste und nicht
 *   eine Seite mit einem leeren Beitrag: sonst bekäme jede Etappe ohne Eintrag
 *   einen leeren Absatz.
 */
export function splitBeitraege(
  beitraege: readonly DruckBeitrag[],
  ersteZeilen: number,
  folgeZeilen: number,
): BeitragPlan {
  const warteschlange: DruckBeitrag[] = []
  for (const beitrag of beitraege) {
    const text = beitrag.text.trim()
    if (text) warteschlange.push({ vorspann: beitrag.vorspann, text })
  }
  if (warteschlange.length === 0) return { seiten: [], ungedruckt: 0 }

  const budget = (nr: number) => Math.max(1, Math.floor(nr === 0 ? ersteZeilen : folgeZeilen))

  const seiten: DruckBeitrag[][] = []
  let seite: DruckBeitrag[] = []
  let nummer = 0
  let übrig = budget(0)

  const umbruch = () => {
    seiten.push(seite)
    seite = []
    nummer++
    übrig = budget(nummer)
  }

  while (warteschlange.length > 0) {
    // Die Reissleine: mehr Seiten als das kann ein Tagebucheintrag nicht
    // rechtfertigen, und eine Endlosschleife im Aufbau der Seiten wäre ein
    // hängender Reiter ohne Fehlermeldung.
    const letzte = nummer >= MAX_FORTSETZUNGEN
    const beitrag = warteschlange[0]!
    // Der Abstand fällt nur zwischen zwei Beiträgen an. Ganz oben auf einer
    // Seite kostet er nichts.
    const abstand = seite.length > 0 ? BEITRAG_ABSTAND_ZEILEN : 0

    if (beitragZeilen(beitrag) + abstand <= übrig) {
      seite.push(beitrag)
      übrig -= beitragZeilen(beitrag) + abstand
      warteschlange.shift()
      continue
    }

    // Abgerundet, und das ist keine Vorsicht, sondern Notwendigkeit: die
    // Abstände zwischen den Beiträgen sind Bruchteile einer Zeile, gesetzt
    // werden aber ganze. Wer mit 4.7 Zeilen schneidet, schneidet 4.7 mal die
    // Zeilenbreite an Zeichen ab - und das sind fünf Zeilen, also eine mehr,
    // als noch Platz hat. Genau so entsteht der stumme Überlauf wieder.
    //
    // Reicht der Rest der Seite für den Vorspann und die erste Zeile nicht
    // mehr, fängt der Beitrag auf der nächsten Seite an, statt hier als
    // Kopfzeile ohne Inhalt zu stehen.
    const platz = Math.floor(übrig - abstand - (letzte ? REST_HINWEIS_ZEILEN : 0))
    if (platz < 1) {
      if (seite.length === 0) {
        // Eine leere Seite und trotzdem kein Platz - dann ist das Budget selbst
        // zu klein. Eine Zeile muss gesetzt werden, sonst kommt das Verteilen
        // nie voran.
        seite.push(...schneide(beitrag, 1, warteschlange))
      } else if (letzte) {
        break
      } else {
        umbruch()
        continue
      }
    } else {
      seite.push(...schneide(beitrag, platz, warteschlange))
    }

    if (letzte) break
    umbruch()
  }

  // Eine angefangene letzte Seite kommt dazu, eine leere nicht: die entstünde,
  // wenn das letzte Stück genau aufgegangen ist, und wäre eine Seite ohne
  // alles im Buch.
  if (seite.length > 0) seiten.push(seite)

  // Was jetzt noch in der Warteschlange steht, wird nicht stumm angehängt,
  // sondern gemeldet.
  let ungedruckt = 0
  for (const beitrag of warteschlange) ungedruckt += beitrag.text.length
  return { seiten, ungedruckt }
}

/**
 * Schneidet den vordersten Beitrag auf so viele Zeilen zurück und legt den Rest
 * wieder vorn in die Warteschlange.
 *
 * @returns das Stück, das auf die laufende Seite kommt.
 */
function schneide(
  beitrag: DruckBeitrag,
  zeilen: number,
  warteschlange: DruckBeitrag[],
): DruckBeitrag[] {
  const mess = messtext(beitrag)
  const vorlauf = mess.length - beitrag.text.length
  // Der Schnitt fällt im Messtext und gilt für den echten: der Vorlauf steht
  // nur dort und muss wieder abgezogen werden. Mindestens ein Zeichen, sonst
  // gibt es keinen Fortschritt.
  const schnitt = Math.max(1, schnittFuerZeilen(mess, zeilen) - vorlauf)

  const kopf = beitrag.text.slice(0, schnitt).trimEnd()
  const rest = beitrag.text.slice(schnitt).trimStart()

  if (rest) warteschlange[0] = { vorspann: '', text: rest }
  else warteschlange.shift()

  return kopf ? [{ vorspann: beitrag.vorspann, text: kopf }] : []
}

/**
 * Schneidet einen einzelnen Text in Stücke, die auf je eine Seite passen.
 *
 * Der Sonderfall von splitBeitraege mit genau einem Beitrag ohne Vorspann - und
 * er delegiert dorthin, statt dieselbe Rechnung ein zweites Mal zu führen.
 * Zwei Fassungen davon liefen mit der Zeit auseinander, und die eine, die dann
 * falsch rechnet, schneidet Text weg.
 *
 * @param ersteZeilen Zeilen für das erste Stück.
 * @param folgeZeilen Zeilen für jedes weitere.
 */
export function splitText(text: string, ersteZeilen: number, folgeZeilen: number): TextPlan {
  const plan = splitBeitraege([{ vorspann: '', text }], ersteZeilen, folgeZeilen)
  return {
    stuecke: plan.seiten.map((seite) => seite.map((beitrag) => beitrag.text).join('\n')),
    ungedruckt: plan.ungedruckt,
  }
}

/**
 * Bis wohin der Text reicht, wenn er so viele Zeilen belegen darf.
 *
 * Gibt einen Index zurück, an dem an einer Wortgrenze geschnitten werden kann,
 * und niemals 0 - ein Stück ohne Fortschritt wäre eine Endlosschleife.
 */
function schnittFuerZeilen(text: string, zeilen: number): number {
  const breite = ZEICHEN_JE_ZEILE
  let uebrig = zeilen
  let i = 0

  while (i < text.length && uebrig > 0) {
    const umbruch = text.indexOf('\n', i)
    const ende = umbruch < 0 ? text.length : umbruch
    const noetig = Math.max(1, Math.ceil((ende - i) / breite))

    if (noetig > uebrig) {
      // Der Abschnitt passt nur teilweise: so viele volle Zeilen, wie noch da
      // sind, und dann an der Wortgrenze davor abbrechen.
      return Math.max(1, wortgrenze(text, i + uebrig * breite))
    }

    uebrig -= noetig
    i = umbruch < 0 ? text.length : umbruch + 1
  }

  return Math.max(1, Math.min(i, text.length))
}

/** Letztes Wortende bis einschliesslich grenze, sonst harter Schnitt. */
function wortgrenze(text: string, grenze: number): number {
  const fenster = text.slice(0, grenze + 1)
  const leer = fenster.search(/\s\S*$/)
  return leer > 0 ? leer : Math.min(grenze, text.length)
}

export interface StageInput {
  /**
   * Die Tagebuchbeiträge dieser Etappe, in Leserichtung und schon mit der
   * Entscheidung über den Vorspann - siehe druckBeitraege.
   */
  beitraege: readonly DruckBeitrag[]
  /** Wie viele Fotos an der Etappe hängen. */
  photoCount: number
  /** Wie viele Tulpen an der Etappe hängen. Fehlt der Wert, sind es keine. */
  tulipCount?: number
  /** Startort, für die Frage, ob die Kopfzeile umbricht. */
  from?: string
  /** Zielort, dito. */
  to?: string
  /** Was in den Planungsspalten steht. Fehlt es, sind die Spalten leer. */
  planung?: PlanungInput
}

/**
 * Der vollständige Plan einer Etappe.
 *
 * Erst die Planungsspalten - sie sind die einzige Grösse, die auch die Wahl der
 * Vorlage beeinflusst -, dann die Vorlage, dann die Tulpen, dann der Text in
 * Seitenstücke, dann die Fotos. Die Reihenfolge der Zusatzseiten ist die des
 * Blätterns und damit eine Aussage: zuerst die Roadbookseiten, dann die
 * Textfortsetzungen, zuletzt die Bilder.
 *
 * Die Roadbookseiten stehen vorn, weil sie Fahranweisung sind und nicht
 * Erinnerung. Wer unterwegs die Kehren des Vrsic sucht, blättert eine Seite
 * weiter und nicht an vier Seiten Tagebuch vorbei; dass der Eintrag des Abends
 * dadurch eine Seite später weitergeht, kostet beim Lesen daheim nichts.
 * Danach gilt wieder die alte Ordnung: den Tag zu Ende lesen, dann die Bilder.
 */
export function planStage(input: StageInput): StagePlan {
  const tulpen = Math.max(0, Math.floor(input.tulipCount ?? 0))
  const planung = planPlanung(input.planung ?? LEERE_PLANUNG)
  const zeilen = beitraegeZeilen(input.beitraege)

  const last: SeitenLast = {
    planung: planung.zeilen,
    kopf: kopfZeilen(input.from ?? '', input.to ?? ''),
    tulpen: 0,
    fotos: input.photoCount > 0,
  }

  const template = chooseTemplate(zeilen, input.photoCount, tulpen, last)
  const tulips = placeTulips(tulpen, template, zeilen, last)
  const platz = seitenKapazitaet(template, { ...last, tulpen: tulips.first.length })
  const text = splitBeitraege(input.beitraege, platz, FORTSETZUNG_ZEILEN)
  const fotos = placePhotos(input.photoCount, template)

  // Der Typ steht an der Initialisierung und nicht erst am Anhängen: aus einer
  // leeren Liste von Seiten ohne Fotos schliesst TypeScript sonst auf
  // photos: never[], und die Bildseiten passen dann nicht mehr hinein.
  const continuations: StagePagePlan[] = []
  for (const seite of tulips.extraPages) {
    continuations.push({ beitraege: [], photos: [], tulips: seite })
  }
  for (const stueck of text.seiten.slice(1)) {
    continuations.push({ beitraege: stueck, photos: [], tulips: [] })
  }
  for (const photos of fotos.extraPages) {
    continuations.push({ beitraege: [], photos, tulips: [] })
  }

  const first: StagePagePlan = {
    beitraege: text.seiten[0] ?? [],
    photos: fotos.first,
    tulips: tulips.first,
  }

  /*
   * Der Hinweis auf ungedruckten Text gehört auf die Seite, auf der der Text
   * abbricht - das ist die letzte Textseite der Etappe, nicht die letzte Seite
   * überhaupt. Wer den Tag zu Ende liest, soll dort erfahren, dass noch etwas
   * fehlt, und nicht drei Bildseiten später.
   */
  if (text.ungedruckt > 0) {
    const letzteText = [...continuations].reverse().find((seite) => seite.beitraege.length > 0)
    if (letzteText) letzteText.ungedruckt = text.ungedruckt
    else first.ungedruckt = text.ungedruckt
  }

  return { template, first, continuations, planung }
}
